/** @jsx jsx */

import { css, jsx } from "@emotion/core"
import React, { useEffect, useMemo, useState } from "react"
import { useSkeem } from "../providers/SkeemProvider"
import { IRecord } from "../types/schema"
import { Icon, IconButton, Spacer, Spinner } from "@cd2/components"
import { icons } from "../utils/icons"
import { ADMIN_COMPANY_TAG_LABEL, adminCompanyTagColors } from "../utils/adminCompanyTagStyles"
import { AdminCompanyTagPill } from "../utils/AdminCompanyTagPill"
import { Popover } from "./Popover"

type Tag = IRecord<"companyTags", "name">

type Option = { kind: "tag"; tag: Tag } | { kind: "create"; name: string }

export function CompanyTagsInlineEditor({
  companyId,
  initialTags,
  onUpdated,
}: {
  companyId: string
  initialTags: Tag[]
  onUpdated(): void
}) {
  const skeem = useSkeem()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [query, setQuery] = useState("")
  const [allTags, setAllTags] = useState<Tag[] | null>(null)
  const [selectedTags, setSelectedTags] = useState<Tag[]>(initialTags)
  const [openedWithTags, setOpenedWithTags] = useState<Tag[]>(initialTags)

  useEffect(() => {
    if (!open) {
      setSelectedTags(initialTags)
    }
  }, [JSON.stringify(initialTags), open])

  async function ensureTagsLoaded() {
    if (allTags && allTags.length > 0) return
    setLoading(true)
    const tags = await skeem.fetch2<Tag[]>({
      model: {
        name: "companyTags",
        attributes: ["name"],
        sort: { by: "name", direction: "asc" },
        paginate: { size: 200 },
      },
    })
    setAllTags(tags)
    setLoading(false)
  }

  const options = useMemo(() => {
    const normalizedQuery = query.trim()
    const lowerQuery = normalizedQuery.toLowerCase()

    const tagOptions: Option[] = (allTags ?? [])
      .filter(tag => {
        if (!normalizedQuery) return true
        return tag.name.toLowerCase().includes(lowerQuery)
      })
      .map(tag => ({ kind: "tag" as const, tag }))
    if (!normalizedQuery) return tagOptions

    const exists = (allTags ?? []).some(t => t.name.toLowerCase() === normalizedQuery.toLowerCase())
    if (exists) return tagOptions

    return [{ kind: "create" as const, name: normalizedQuery }, ...tagOptions]
  }, [allTags, query])

  async function saveCompanyTags(nextTags: Tag[]) {
    await skeem.mutate({
      companies: {
        update: {
          id: companyId,
          companyTags: { set: nextTags.map(t => t.id) },
        },
      },
    })
    setSelectedTags(nextTags)
    onUpdated()
  }

  function isSelected(tag: Tag) {
    return selectedTags.some(t => t.id === tag.id)
  }

  function toggleTag(tag: Tag) {
    if (isSelected(tag)) {
      setSelectedTags(selectedTags.filter(t => t.id !== tag.id))
    } else {
      setSelectedTags([...selectedTags, tag].sort((a, b) => a.name.localeCompare(b.name)))
    }
  }

  async function createAndSelect(name: string) {
    const trimmed = name.trim()
    if (!trimmed) return

    const existing = (allTags ?? []).find(t => t.name.toLowerCase() === trimmed.toLowerCase())
    if (existing) {
      if (!isSelected(existing)) toggleTag(existing)
      setQuery("")
      return
    }

    setLoading(true)
    const created = await skeem.mutate<Tag>({ companyTags: { create: { name: trimmed } } })
    setAllTags(old => [...(old ?? []), created].sort((a, b) => a.name.localeCompare(b.name)))
    setLoading(false)
    setSelectedTags([...selectedTags, created].sort((a, b) => a.name.localeCompare(b.name)))
    setQuery("")
  }

  function tagsEqualById(a: Tag[], b: Tag[]) {
    const aIds = a.map(t => t.id).sort()
    const bIds = b.map(t => t.id).sort()
    if (aIds.length !== bIds.length) return false
    for (let i = 0; i < aIds.length; i++) {
      if (aIds[i] !== bIds[i]) return false
    }
    return true
  }

  async function persistIfNeededAndClose() {
    try {
      setSaving(true)
      let nextTags = selectedTags

      const trimmed = query.trim()
      if (trimmed) {
        // If the user has typed something and hits Save, treat it as an intent to create/select
        // the tag (unless it already exists / is already selected).
        await ensureTagsLoaded()

        const existing = (allTags ?? []).find(t => t.name.toLowerCase() === trimmed.toLowerCase())
        if (existing) {
          if (!nextTags.some(t => t.id === existing.id)) {
            nextTags = [...nextTags, existing].sort((a, b) => a.name.localeCompare(b.name))
            setSelectedTags(nextTags)
          }
        } else {
          // Create + include it
          setLoading(true)
          const created = await skeem.mutate<Tag>({ companyTags: { create: { name: trimmed } } })
          setAllTags(old => [...(old ?? []), created].sort((a, b) => a.name.localeCompare(b.name)))
          setLoading(false)

          nextTags = [...nextTags, created].sort((a, b) => a.name.localeCompare(b.name))
          setSelectedTags(nextTags)
        }
      }

      const didChange = !tagsEqualById(openedWithTags, nextTags)
      if (didChange) {
        await saveCompanyTags(nextTags)
      }
      setOpen(false)
      setQuery("")
    } finally {
      setSaving(false)
    }
  }

  function handleClose() {
    // Auto-save on close (good UX for quick table edits)
    void persistIfNeededAndClose()
  }

  const filteredTagOptions = options.filter(opt => opt.kind === "tag") as {
    kind: "tag"
    tag: Tag
  }[]
  const createOption = options.find(opt => opt.kind === "create") as
    | { kind: "create"; name: string }
    | undefined

  return (
    <Popover
      visible={open}
      placement="bottom-left"
      onHidden={handleClose}
      getContent={() => (
        <div
          css={css`
            width: 360px;
            max-width: min(360px, calc(100vw - 48px));
            background: white;
            border: 1px solid #e6e6e6;
            border-radius: 8px;
            padding: 10px;
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 8px;
            `}
          >
            <div
              css={css`
                font-weight: 700;
              `}
            >
              {ADMIN_COMPANY_TAG_LABEL}
            </div>
            <IconButton icon={icons.cancel} buttonStyle="text" onClick={handleClose} />
          </div>
          <Spacer size={8} />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search or create…"
            css={css`
              width: 100%;
              border: 1px solid #ddd;
              border-radius: 6px;
              padding: 8px 10px;
              outline: none;
              :focus {
                border-color: #b6c2ff;
              }
            `}
            onKeyDown={e => {
              if (e.key === "Enter" && createOption) {
                e.preventDefault()
                createAndSelect(createOption.name)
              } else if (e.key === "Escape") {
                e.preventDefault()
                handleClose()
              }
            }}
          />
          <Spacer size={8} />
          <div
            css={css`
              display: flex;
              flex-wrap: wrap;
              gap: 6px;
              max-height: 160px;
              overflow: auto;
              padding: 4px;
              border: 1px solid #f0f0f0;
              border-radius: 6px;
              background: #fafafa;
            `}
          >
            {selectedTags.length === 0 ? (
              <span
                css={css`
                  color: #888;
                  font-size: 12px;
                `}
              >
                No tags selected
              </span>
            ) : (
              selectedTags.map(tag => (
                <AdminCompanyTagPill
                  key={tag.id}
                  name={tag.name}
                  onClick={() => toggleTag(tag)}
                  interactive
                />
              ))
            )}
          </div>
          <Spacer size={10} />
          <div
            css={css`
              max-height: 240px;
              overflow: auto;
              border: 1px solid #eee;
              border-radius: 6px;
            `}
          >
            {loading ? (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  padding: 10px;
                `}
              >
                <Spinner />
                Loading tags…
              </div>
            ) : (
              <div>
                {createOption && (
                  <div
                    css={css`
                      padding: 10px;
                      cursor: pointer;
                      font-weight: 700;
                      color: #1d4ed8;
                      border-bottom: 1px solid #eee;
                      :hover {
                        background: #f5f8ff;
                      }
                    `}
                    onClick={() => createAndSelect(createOption.name)}
                  >
                    Create “{createOption.name}”
                  </div>
                )}
                {filteredTagOptions.length === 0 ? (
                  <div
                    css={css`
                      padding: 10px;
                      color: #777;
                      font-size: 12px;
                    `}
                  >
                    No tags found
                  </div>
                ) : (
                  filteredTagOptions.map(({ tag }) => (
                    <div
                      key={tag.id}
                      css={css`
                        padding: 10px;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 10px;
                        cursor: pointer;
                        :hover {
                          background: #f7f7f7;
                        }
                      `}
                      onClick={() => {
                        if (saving) return
                        toggleTag(tag)
                      }}
                    >
                      <span
                        css={css`
                          display: inline-flex;
                          align-items: center;
                          gap: 8px;
                        `}
                      >
                        <Icon
                          icon={icons.companyTags}
                          prefix="solid"
                          color={adminCompanyTagColors.iconColor}
                        />
                        {tag.name}
                      </span>
                      {isSelected(tag) && <span>✓</span>}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}
      interactive
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          max-width: 360px;
          cursor: pointer;
        `}
        onClick={async e => {
          e.stopPropagation()
          if (!open) {
            setSelectedTags(initialTags)
            setOpenedWithTags(initialTags)
            setOpen(true)
            await ensureTagsLoaded()
          }
        }}
      >
        <div
          css={css`
            display: flex;
            gap: 6px;
            flex-wrap: wrap;
            align-items: center;
            max-width: 320px;
          `}
        >
          {initialTags.length === 0 ? (
            <span
              css={css`
                color: #999;
                font-size: 12px;
              `}
            >
              —
            </span>
          ) : (
            initialTags.slice(0, 3).map(tag => <AdminCompanyTagPill key={tag.id} name={tag.name} />)
          )}
          {initialTags.length > 3 && (
            <span
              css={css`
                color: #666;
                font-size: 12px;
              `}
            >
              +{initialTags.length - 3}
            </span>
          )}
        </div>
        <IconButton icon={icons.edit} buttonStyle="text" />
      </div>
    </Popover>
  )
}
